import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import notFoundImage from '../../assets/fox-not-found.webp';
import { getImageUrl } from '../../lib/helpers';

/**
 * Foto de socio: miniatura pequeña + popover con la foto GRANDE al pasar el ratón
 * (desktop) o tocar (tablet). Pensado para listas donde la cara es difícil de
 * identificar (Entradas, Nueva entrada, Dispensario, dropdown de Productos).
 *
 * Dos punteros, dos gestos (12-09-2026, reporte de Yago en preview):
 *  - Ratón: pasar por encima abre, salir cierra; el clic la deja abierta (antes
 *    alternaba y, como el hover ya la había abierto, «clic en la foto» la cerraba).
 *  - Dedo: un toque abre, otro toque o tocar fuera cierra. iOS emula mouseenter y
 *    mouseleave alrededor de cada toque: con el dedo esos eventos se ignoran, o el
 *    mismo gesto abría y cerraba. El toque nunca llega a la fila (la ficha no se abre).
 *
 * - `path`: documents_path / image_path del socio (clave o URL). Si falta → fox.
 * - `name`: se muestra bajo la foto grande.
 * - `className`: clases de la miniatura (tamaño/forma).
 * - `size`: lado del popover en px.
 *
 * El popover se renderiza en un portal (document.body) para que no lo recorte el
 * overflow de tablas/listas. pointer-events:none para no interferir con la fila.
 */

/**
 * La miniatura de una FILA de listado (Entradas, Socios, Personal): cuadrado con
 * vértices redondeados y borde fino. Una sola cadena para que las tres tablas se vean
 * igual (pedido el 11-09-2026: Socios y Personal llevaban un círculo sin zoom).
 */
export const FOTO_DE_FILA = 'w-10 h-10 rounded-lg object-cover border border-[#E5E5E5] flex-shrink-0';

/** La foto de una TARJETA (iPad vertical / móvil, 128 px): misma foto grande al tocar. */
export const FOTO_DE_TARJETA = 'w-32 h-32 rounded-xl object-cover border-2 border-gray-200 flex-shrink-0';

export default function MemberPhoto({
  path,
  name = '',
  className = 'h-10 w-10 rounded-full object-cover',
  size = 320,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  // Durante el segundo que sigue a un toque, el gesto es con el dedo: los mouseenter /
  // mouseleave que iOS emula alrededor de cada toque no cuentan. Ventana y no
  // interruptor, para que un portátil táctil o un iPad con trackpad recuperen el ratón.
  const ultimoToqueRef = useRef(0);
  const conElDedo = () => Date.now() - ultimoToqueRef.current < 1000;
  const src = path ? getImageUrl(path) : notFoundImage;

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = r.right + 8;                 // por defecto a la derecha de la miniatura
    if (left + size > window.innerWidth - 8) left = r.left - size - 8; // si no cabe, a la izquierda
    if (left < 8) left = 8;
    let top = r.top + r.height / 2 - size / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - size - 48));
    setPos({ top, left });
  }, [size]);

  const show = useCallback(() => { place(); setOpen(true); }, [place]);
  const hide = useCallback(() => setOpen(false), []);

  // Cerrar al tocar fuera (tablet) o al hacer scroll. El click que abre no debe
  // cerrarlo en el mismo tick → se difiere el alta del listener.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener('click', close, { once: true }), 0);
    window.addEventListener('scroll', close, true);
    return () => { clearTimeout(t); document.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [open]);

  return (
    <>
      <img
        ref={ref}
        src={src}
        alt={name}
        onError={(e) => { e.target.src = notFoundImage; }}
        onTouchStart={() => { ultimoToqueRef.current = Date.now(); }}
        onMouseEnter={() => { if (!conElDedo()) show(); }}
        onMouseLeave={() => { if (!conElDedo()) hide(); }}
        onClick={(e) => {
          e.stopPropagation();
          if (conElDedo()) { if (open) hide(); else show(); } else show();
        }}
        className={`${className} cursor-zoom-in`}
      />
      {open && createPortal(
        <div
          className="fixed z-[100] pointer-events-none rounded-xl border border-[#E5E5E5] bg-white p-1 shadow-2xl"
          style={{ top: pos.top, left: pos.left, width: size }}
        >
          <img
            src={src}
            alt={name}
            onError={(e) => { e.target.src = notFoundImage; }}
            className="w-full rounded-lg object-cover"
            style={{ height: size }}
          />
          {name && (
            <div className="px-1 pt-1 text-center text-xs font-medium text-[#0A0A0A] truncate">{name}</div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
